/* SeedSource — the demo, in memory, behind the SAME interface the API is behind.
 *
 * This is what makes the demo and the product one app: the React screens do not know
 * which one they are talking to. So this class has to behave like a SERVER, not like a
 * convenient local object:
 *
 *   · every list filters, sorts and PAGES on this side. `getMembers()` returning the
 *     whole roster is the habit that must not survive (MIGRATION.md §6).
 *   · the default is CURRENT members. History is asked for.
 *   · a suppressed member is never scored and never ranked, and is still returned
 *     everywhere a member belongs (SITE-RULES 1).
 *   · every write appends to the event stream, and the stored risk score is recomputed
 *     from shared/model/risk.ts — a cache of the one model, never a second one.
 */

/* SCORE_RED and openCancellations are values, the rest are types. */
import {
  SCORE_RED, canResolve, cancellationOpen, firstNinety, macroSummary, isOnboarding, openCancellation,
  openCancellations, resolve,
} from '@acrostic/model';
import type {
  Anchor, Campaign, CancellationResolution, EventKind, FirstNinety, FollowUp, FollowUpStatus, Gym, GymClass,
  ISODate, Lead, LeadStage, MacroTargets, Member, MemberStatus, RegisterFacts, RiskResult, Role, Scan,
  TimelineEvent, UUID, User, Visit,
} from '@acrostic/model';
import { DEFAULT_PAGE_SIZE, MAX_PAGE } from './contract.js';
import type { ConversationThread } from './contract.js';
import type {
  Account, CampaignDraft, CampaignView, CellPeople, ContextGroup, ContextPerson, DashboardPayload, DataSource,
  ApprovalQueue, DryRun, JourneyCell, LeadPage, LeadQuery, MemberQuery, NewLeadDraft, Page, Register, RegisterRow,
  Schedule, SegmentView, SessionRow, StaffView, Worth, WorthSort,
} from './contract.js';
import { makeAnchor, primaryAnchor } from '@acrostic/model/anchors';
import { LIFECYCLE_CELLS, ONBOARDING_DAYS, lifecycleCellPeople, lifecycleCounts } from '@acrostic/model/lifecycle';
import { CONTEXT_GROUPS, groupsFor, registerContext } from '@acrostic/model/register';
import { SEGMENTS, isDormant, isScored, notScoredBecause, segmentMembers } from '@acrostic/model/segments';
import type { Permission } from '@acrostic/model/permissions';
import { isDefaultFor, permissionsOf } from '@acrostic/model/permissions';
import type { SegmentRule } from '@acrostic/model/segments';
import { daysSince } from '@acrostic/model/time';
import type { LifecycleCellKey } from '@acrostic/model/lifecycle';
import type { LeadRecord, MemberRecord } from '@acrostic/model/records';
import { riskModel } from '@acrostic/model/risk';

import { canSetVisibility, grantableBy as seatsGrantableBy, seesCommercials } from '../seats.js';
import { build } from '@acrostic/seed/build';
import { Clock } from '@acrostic/seed/clock';
import { uuidFrom } from '@acrostic/seed/ids';
import type { BuildOptions, SeededEvent, SeedGym } from '@acrostic/seed/model';

/* Which seat is signed in, across a reload. sessionStorage rather than localStorage:
   a demo left open in a tab should stay where it was, and a demo opened next week
   should start from the sign-in card that explains the four seats.

   Wrapped, because storage THROWS rather than returning null in a private window and
   under some embedded viewers. A demo that white-screens because it could not
   remember a seat is worse than one that asks you to pick again. */
const SEAT_KEY = 'acrostic.seat';
function readSeat(): string | null {
  try { return sessionStorage.getItem(SEAT_KEY); } catch { return null; }
}
function writeSeat(email: string | null): void {
  try {
    if (email) sessionStorage.setItem(SEAT_KEY, email);
    else sessionStorage.removeItem(SEAT_KEY);
  } catch { /* nothing to do: the seat is simply not remembered */ }
}

/* No endpoint ever returns the whole roster. The ceiling is a fact about the seam
   and lives in the contract; this re-export is so the screens that page keep one
   import. */
export { DEFAULT_PAGE_SIZE, MAX_PAGE } from './contract.js';

const DEMO_PASSWORD = 'demo';

/* The four seats the demo advertises on its sign-in card, and the one password.
 *
 * Exported from HERE and not added to the DataSource contract. "List the accounts I
 * will let you sign in as" is a fact about a seeded gym, not a question any real
 * source should answer, and putting it on the contract would oblige ApiSource to
 * either implement it or throw. The sign-in screen imports this directly, which is
 * honest about it being demo furniture.
 *
 * Gym-neutral seats only: the demo is walked through by prospects, and an
 * Acrostic-branded account would show our internal seats inside someone else's gym. */
export const DEMO_ACCOUNTS = [
  { seat: 'Support Coach', name: 'Coach Dana R.',  email: 'support@yourgym.com' },
  { seat: 'Coach',         name: 'Coach Marcus J.', email: 'coach@yourgym.com' },
  { seat: 'Manager',       name: 'Nina Alvarez',    email: 'manager@yourgym.com' },
  { seat: 'Owner',         name: 'Simone Okafor',   email: 'owner@yourgym.com' },
] as const;

export { DEMO_PASSWORD };

export interface SeedSourceOptions extends BuildOptions {
  /** Skip the login screen: open on this seat. Matched on email, then on role. */
  as?: string;
}

export class SeedSource implements DataSource {
  private db!: SeedGym;
  private clock!: Clock;
  private byMember = new Map<UUID, SeededEvent[]>();
  private byLead = new Map<UUID, SeededEvent[]>();
  private session: { user: User; gym: Gym } | null = null;
  private readonly opts: SeedSourceOptions;

  constructor(opts: SeedSourceOptions = {}) {
    this.opts = opts;
  }

  /* ── Lifecycle ─────────────────────────────────────────────────────────── */

  async init(): Promise<void> {
    if (this.db) return;
    this.db = build(this.opts);
    this.clock = new Clock(this.db.meta.now);
    this.reindex();
    /* Who is signed in survives a reload. The gym does NOT: it is rebuilt from the
       same seed, so every id is the same and a bookmarked record still resolves, but
       anything written in the last visit is gone. That is the honest behaviour for a
       demo with no server, and it is better than the alternative, which is being
       thrown back to the sign-in card every time somebody refreshes mid-walkthrough. */
    const remembered = this.opts.as ?? readSeat();
    if (remembered) {
      const u = this.db.users.find((x) => x.email === remembered)
        ?? this.db.users.find((x) => x.roles.includes(remembered as Role));
      if (u) this.session = { user: u, gym: this.db.gym };
    }
  }

  /* WHAT A MEMBER LOOKS LIKE TO WHOEVER IS ASKING.
   *
   * A floor seat is never sent a plan, a price or a renewal date. Applied HERE, at
   * the boundary where a member leaves the source, rather than in the twelve screens
   * that render one: a screen cannot withhold what it was handed, and a screen that
   * is never handed it cannot leak it by accident. The ApiSource will do the same
   * thing in SQL, which is the point of putting it at this seam.
   *
   * The gym's own copy is untouched, so the risk model, the lifecycle strip and the
   * owner's MRR all still read the real numbers. Only the answer is redacted. */
  private get showsMoney(): boolean {
    return seesCommercials(this.session?.user.roles, this.db.gym.settings);
  }

  private seen<T extends Member>(m: T): T {
    if (this.showsMoney) return m;
    return { ...m, plan: null, priceCents: null, membershipExpires: null };
  }

  private seenAll<T extends Member>(rows: T[]): T[] {
    return this.showsMoney ? rows : rows.map((m) => this.seen(m));
  }

  /* A LEAD'S VALUE IS THE SAME FACT ONE STEP EARLIER. "$549/mo intent" on a card is
     what that person WILL pay, and a support coach who chases the 549 harder than
     the 199 is making exactly the distinction the member rule exists to stop. It
     was left in when the member fields went; it should not have been. */
  private seenLead(l: Lead): Lead {
    return this.showsMoney ? l : { ...l, valueCents: null };
  }

  private seenLeads(rows: Lead[]): Lead[] {
    return this.showsMoney ? rows : rows.map((l) => this.seenLead(l));
  }

  /** The built gym, for tests and for anything that wants the whole tenant at once.
   *  Screens must not reach for this: they go through the interface like the API. */
  get gym(): SeedGym {
    return this.db;
  }

  private reindex(): void {
    this.byMember.clear();
    this.byLead.clear();
    for (const e of this.db.events) {
      const key = e.memberId ?? e.leadId;
      if (!key) continue;
      const map = e.memberId ? this.byMember : this.byLead;
      const list = map.get(key);
      if (list) list.push(e);
      else map.set(key, [e]);
    }
  }

  async getSession(): Promise<{ user: User; gym: Gym; today: ISODate } | null> {
    /* The seeded gym's clock is the only day there is here, and the screens must read
       it from the same place they would read the server's. */
    return this.session ? { ...this.session, today: this.clock.today() } : null;
  }

  async login(email: string, password: string): Promise<{ user: User; gym: Gym }> {
    const u = this.db.users.find((x) => x.email.toLowerCase() === String(email).trim().toLowerCase());
    if (!u || password !== DEMO_PASSWORD) throw new Error('Wrong email or password');
    this.session = { user: u, gym: this.db.gym };
    writeSeat(u.email);
    return this.session;
  }

  async logout(): Promise<void> {
    this.session = null;
    writeSeat(null);
  }

  /* ── Members ───────────────────────────────────────────────────────────── */

  async getMembers(q: MemberQuery = {}): Promise<Page<Member>> {
    const status = q.status ?? 'current';
    let rows = this.db.members.filter((m) => matchStatus(m.status, status));

    if (q.q) {
      const needle = q.q.trim().toLowerCase();
      rows = rows.filter((m) =>
        m.name.toLowerCase().includes(needle)
        || (m.email ?? '').toLowerCase().includes(needle)
        || (m.phone ?? '').includes(needle));
    }
    if (q.coachId) rows = rows.filter((m) => m.coachId === q.coachId);
    if (q.plan) rows = rows.filter((m) => m.plan === q.plan);
    if (q.supportCoachId) rows = rows.filter((m) => m.supportCoachId === q.supportCoachId);
    /* PT is a relationship, not a plan name, and a member nobody has marked is not
       a PT client - so `false` means "everybody else", never "unknown". */
    if (q.pt != null) rows = rows.filter((m) => !!m.pt === q.pt);
    if (q.tier) {
      /* A suppressed member has no tier, so a tier filter cannot return one. That is
         rule 1 holding at the query, not at the screen. */
      rows = rows.filter((m) => !m.suppression && m.risk?.tier === q.tier);
    }

    rows = sortMembers(rows, q.sort);

    const size = Math.min(Math.max(1, q.size ?? DEFAULT_PAGE_SIZE), MAX_PAGE);
    const page = Math.max(1, q.page ?? 1);
    const start = (page - 1) * size;
    return { items: this.seenAll(rows.slice(start, start + size)), total: rows.length, page, size };
  }

  async searchPeople(q: string): Promise<{ members: Member[]; leads: Lead[] }> {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return { members: [], leads: [] };
    const hit = (...fields: (string | null)[]): boolean =>
      fields.some((f) => (f ?? '').toLowerCase().includes(needle));
    return {
      members: this.seenAll(this.db.members.filter((m) => hit(m.name, m.email, m.phone)).slice(0, 50)),
      /* EVERY stage, won and lost included: the board shows the open pipeline on
         purpose, so this is the only way back to somebody who joined or said no. */
      leads: this.seenLeads(this.db.leads.filter((l) => hit(l.name, l.email, l.phone)).slice(0, 50)),
    };
  }

  async getMember(id: UUID): Promise<MemberRecord | null> {
    const m = this.db.members.find((x) => x.id === id);
    return m ? this.seen(this.recordFor(m)) : null;
  }

  /* WHAT NEEDS SIGNING OFF, the same list as the server's NEEDS_MEMBER_EDIT. A seat
     without `member_edit` has these RAISED rather than written, and the ungated half
     of the same patch still goes through. Mirrored here because a demo that lets a
     coach change a price is a demo of a product that does not exist. */

  private static readonly NEEDS_SIGN_OFF: (keyof Member)[] = [
    'status', 'plan', 'priceCents', 'membershipExpires', 'cancelledAt', 'joinedAt',
  ];

  async updateMember(id: UUID, patch: Partial<Member>): Promise<Member> {
    const m = this.mustMember(id);
    const me = this.session?.user;
    const mine = permissionsOf(me?.roles, me?.permissions ?? null);
    if (!mine.includes('member_edit')) {
      const gated = SeedSource.NEEDS_SIGN_OFF.filter((k) => k in patch);
      if (gated.length) {
        const who = `${m.firstName} ${m.lastName}`.trim();
        this.db.approvals.unshift({
          id: uuidFrom('approval:raised:' + this.db.approvals.length),
          gymId: this.db.gym.id,
          kind: 'priceCents' in patch || 'plan' in patch ? 'plan change' : 'status change',
          /* WHAT IS BEING ASKED FOR, in the words the queue already uses — "Cancel
             membership", "Price $349 → $299/mo". This used to read
             "Paige Clark: status", which both repeated the name the next column
             prints and failed to say what the change actually was, leaving the one
             person who has to decide it unable to decide it from the row. */
          summary: describeChange(m, patch, gated),
          memberId: id, leadId: null,
          requestedBy: me?.name ?? '',
          requestedAt: this.clock.today(),
          /* The screen reads targetName for "Who it is about" and prints "A member"
             without it. Every seeded approval carries one; the raised ones did not,
             so the only rows in the queue that were anonymous were the real ones. */
          payload: {
            targetName: who,
            patch: Object.fromEntries(gated.map((k) => [k, patch[k]])),
          },
          decision: null,
        });
        for (const k of gated) delete patch[k];
      }
    }
    Object.assign(m, patch);
    if (patch.suppression !== undefined || patch.anchors || patch.lastVisit !== undefined || patch.health !== undefined) {
      this.rescore(m);
    }
    return m;
  }

  /* ---- the conversation (PRD 6) -----------------------------------------------
     Read out of the event stream, because in the demo there is no Photon to read
     from. The shape is the shape it has against the real thing: ask for a thread,
     render what comes back, and store nothing.

     SENDING IS OFF, and that is the gym's real setting rather than a demo excuse:
     "Sending is off until Acrostic turns it on for a gym, once the number is
     provisioned and registered. Nothing on the screen promises a channel that is
     not live." */
  async getConversation(id: UUID): Promise<ConversationThread> {
    const m = this.mustMember(id);
    /* A thread is matched on the phone number already on the record. No number is
       a data-quality finding, not an empty conversation. */
    if (!m.phone) return { channel: 'unmatched', sending: false, messages: [] };

    const messages = (this.byMember.get(m.id) ?? [])
      .filter((e) => e.kind === 'sms')
      .map((e) => ({
        at: e.at,
        inbound: e.payload?.['inbound'] === true,
        who: e.payload?.['inbound'] === true ? null : (e.who ?? null),
        /* The seeded outbound lines read `Message sent: "..."`, which is how a
           TIMELINE entry says it happened. In the thread the words are the thing,
           so the wrapper comes off rather than being shown to a member's face. */
        text: String(e.detail ?? '').replace(/^Message sent:\s*"(.*)"$/s, '$1'),
      }))
      .filter((msg) => msg.text.length > 0);

    return { channel: 'ok', sending: false, messages };
  }

  /* ---- the first 90 days (PRD 20) --------------------------------------------
     ONE MEMBER, NOT A LIST, AND DELIBERATELY SO. The programme has no screen of its
     own: the track is drawn on the member's own record, in the place the score would
     otherwise be. So this answers for the record that is open rather than building a
     roster nothing renders.

     IT IS COMPUTED HERE RATHER THAN IN THE SCREEN because the evidence is the member's
     event stream, which the record does not otherwise load. The lead track is built
     screen-side for the opposite reason: the lead record already has its stage history
     in hand.

     EVIDENCE IS A TOUCH, NOT A VISIT. A checkpoint is met when somebody did
     something, so attendance is left out on purpose: a member who trained four
     times and was spoken to by nobody has had the gym's schedule missed entirely,
     and counting their own visits as the gym's work would hide exactly that. */
  async getFirstNinety(id: UUID): Promise<FirstNinety | null> {
    const today = this.clock.today();
    const m = this.db.members.find((x) => x.id === id);
    if (!m || m.status === 'cancelled' || !isOnboarding(m, today)) return null;
    /* A touch the GYM made. There is no 'anchor' event kind: recording one appends
       a note, which is already in this set. */
    const TOUCHED = new Set<EventKind>(['call', 'sms', 'email', 'note', 'care', 'outcome', 'followup']);
    const evidence = (this.byMember.get(m.id) ?? [])
      .filter((e) => TOUCHED.has(e.kind))
      .map((e) => e.at);
    return firstNinety(m, evidence, today);
  }

  /* ---- cancellation initiated (PRD 4) ----------------------------------------
     ANYBODY MAY RECORD ONE. There is deliberately no permission check here: it is
     heard rather than decided, and the member who mentions it to whoever is on the
     desk is the commonest case there is. The model refuses an empty `asked`,
     because those words print where the score would otherwise be. */
  async recordCancellation(id: UUID, input: { asked: string }): Promise<Member> {
    const m = this.mustMember(id);
    if (cancellationOpen(m)) {
      throw new Error('This member has already asked. Resolve the open one first.');
    }
    const who = this.session?.user.name ?? 'the front desk';
    m.cancellation = openCancellation({
      asked: input.asked, recordedBy: who, askedAt: this.clock.today(),
    });
    /* THE SCORE GOES, and it is cleared here rather than left to a caller.
       rescore() would put a number back on somebody the product has just decided
       has no number, so this is the one write that nulls it by hand. */
    m.risk = null;
    this.append({
      memberId: m.id, leadId: null,
      at: this.clock.today(), kind: 'cancelled',
      title: 'Asked about cancelling', detail: input.asked,
      who, source: 'Staff', tone: 'risk', icon: 'alert',
      payload: { state: 'raised' },
    });
    return m;
  }

  /* ONLY THE OWNER RESOLVES ONE, and the check is here rather than only on the
     button: a seat is a preset and the nav is about attention, so a screen hiding
     a control is never the thing that makes an action impossible. */
  async resolveCancellation(id: UUID, resolution: CancellationResolution): Promise<Member> {
    const m = this.mustMember(id);
    if (!m.cancellation) throw new Error('There is nothing to resolve on this member.');
    if (!canResolve(this.session?.user.roles)) {
      throw new Error('Only the Owner can close a cancellation.');
    }
    /* The model refuses the half-written ending: an exit code when they left, and
       what changed when they stayed. */
    m.cancellation = resolve(m.cancellation, resolution, this.clock.today());
    this.append({
      memberId: m.id, leadId: null,
      at: this.clock.today(), kind: 'cancelled',
      title: resolution.outcome === 'left' ? 'Left after asking' : 'Stayed after asking',
      detail: resolution.outcome === 'left'
        ? `Reason: ${resolution.reasonCode}`
        : `What changed: ${resolution.whatChanged}`,
      who: resolution.by, source: 'Staff',
      tone: resolution.outcome === 'left' ? 'risk' : 'good', icon: 'reply',
      payload: { state: 'resolved', outcome: resolution.outcome },
    });
    if (resolution.outcome === 'left') {
      m.status = 'cancelled';
      m.cancelledAt = this.clock.today();
    } else {
      /* They stayed, so the model takes them back. Scoring resumes from here
         rather than from whatever the number happened to be before they asked. */
      this.rescore(m);
    }
    return m;
  }

  /** The one event stream (SITE-RULES 11b), newest first — the record renders it that way. */
  async getTimeline(id: UUID, o: { kinds?: EventKind[]; limit?: number } = {}): Promise<TimelineEvent[]> {
    const ofMember = this.byMember.has(id);
    let rows = (this.byMember.get(id) ?? this.byLead.get(id) ?? []).slice();
    if (o.kinds?.length) {
      const want = new Set(o.kinds);
      rows = rows.filter((e) => want.has(e.kind));
    }
    rows.reverse();
    /* THE JOINED EVENT NAMES THE PLAN in its detail, which is the same fact the
       record's Sales tab carries and is withheld in exactly the same way. The event
       still says they joined; it stops saying what they bought.
 
       ON A MEMBER ONLY. A lead's `cancelled` event carries the reason they did not
       join, which is the opposite of a commercial secret: it is the field the
       product refuses to let anybody skip. Blanking it here wiped the one thing
       that write exists to record. */
    if (ofMember && !this.showsMoney) {
      rows = rows.map((e) => (e.kind === 'membership' || e.kind === 'converted' || e.kind === 'cancelled')
        ? { ...e, detail: '' } : e);
    }
    return o.limit ? rows.slice(0, o.limit) : rows;
  }

  async addMemberNote(id: UUID, text: string): Promise<TimelineEvent> {
    const m = this.mustMember(id);
    return this.append({
      memberId: m.id, leadId: null,
      at: this.clock.today(), kind: 'note', title: 'Note', detail: text,
      who: this.session?.user.name ?? m.supportCoach, source: 'Staff', tone: 'plain', icon: 'note',
      payload: {},
    });
  }

  /* DATED FOR THE SCORE. `macrosAt` is what program progress compares with the
     latest scan, so setting targets today makes them current until the next scan
     arrives, and a rescore is what moves the number. */
  async setMemberMacros(id: UUID, t: Omit<MacroTargets, 'setAt' | 'by'>): Promise<Member> {
    const m = this.mustMember(id);
    const today = this.clock.today();
    const who = this.session?.user.name ?? null;
    m.macros = { ...t, setAt: today, by: who };
    m.macrosAt = today;
    this.rescore(m);
    this.append({
      memberId: m.id, leadId: null,
      at: today, kind: 'macros',
      title: 'Macro targets set', detail: macroSummary(m.macros),
      who, source: 'Staff', tone: 'info', icon: 'dashboard',
      payload: {},
    });
    return m;
  }

  /* ── Anchors (SITE-RULES 2) ────────────────────────────────────────────── */

  async addMemberAnchor(id: UUID, a: Omit<Anchor, 'id' | 'lastTwoWay'>): Promise<Anchor | null> {
    const m = this.mustMember(id);
    /* makeAnchor refuses an anchor with no basis. That refusal lives in shared, so the
       demo and the API refuse the same thing for the same reason. */
    const made = makeAnchor(a, this.clock.today());
    if (!made) return null;
    made.id = `${m.id}:anchor:${m.anchors.length}`;
    const existing = m.anchors.findIndex((x) => x.name === made.name);
    if (existing >= 0) m.anchors[existing] = made;
    else m.anchors.push(made);
    m.primaryAnchor = primaryAnchor(m, this.clock.today());
    this.rescore(m);
    this.append({
      memberId: m.id, leadId: null,
      at: this.clock.today(), kind: 'note', title: 'Anchor recorded',
      detail: `${made.name} (${made.role}): ${made.basis}`,
      who: this.session?.user.name ?? null, source: 'Staff', tone: 'good', icon: 'link',
      payload: { anchor: made.name },
    });
    return made;
  }

  /** Somebody spoke to them. Recording it IS the two-way moment, so it decays from here. */
  async touchMemberAnchor(id: UUID, name: string): Promise<Anchor | null> {
    const m = this.mustMember(id);
    const a = m.anchors.find((x) => x.name === name);
    if (!a) return null;
    a.lastTwoWay = this.clock.today();
    m.primaryAnchor = primaryAnchor(m, this.clock.today());
    this.rescore(m);
    return a;
  }

  /** For an anchor recorded in error. An anchor is NEVER deleted to say it went quiet:
   *  that is what decay is for, and decay is a third state, not an absence. */
  async removeMemberAnchor(id: UUID, name: string): Promise<void> {
    const m = this.mustMember(id);
    m.anchors = m.anchors.filter((x) => x.name !== name);
    m.primaryAnchor = primaryAnchor(m, this.clock.today());
    this.rescore(m);
  }

  /* ── Leads ─────────────────────────────────────────────────────────────── */

  async getLeads(q: LeadQuery = {}): Promise<LeadPage> {
    const scope = q.scope ?? 'open';
    let rows = this.db.leads.filter((l) => matchLeadScope(l.stage, scope));
    if (q.stage) rows = rows.filter((l) => l.stage === q.stage);
    if (q.ownerId) rows = rows.filter((l) => l.ownerId === q.ownerId);
    if (q.q) {
      const needle = q.q.trim().toLowerCase();
      rows = rows.filter((l) => l.name.toLowerCase().includes(needle) || (l.email ?? '').toLowerCase().includes(needle));
    }
    rows.sort((a, b) => String(b.lastActivity ?? b.createdAt).localeCompare(String(a.lastActivity ?? a.createdAt)));
    const size = Math.min(Math.max(1, q.size ?? DEFAULT_PAGE_SIZE), MAX_PAGE);
    const page = Math.max(1, q.page ?? 1);
    const start = (page - 1) * size;
    /* Counted off `rows` - everything the filter matched - and never off the slice.
       The board shows the most recent hundred and has to be able to say how many of
       the whole pipeline nobody has been routed to. */
    const unrouted = rows.filter((l) => !l.ownerId).length;
    return {
      items: this.seenLeads(rows.slice(start, start + size)),
      total: rows.length, page, size, unrouted,
    };
  }

  /* A NEW LEAD IS ALWAYS AT STAGE 'new' AND HAS NO ANCHOR. Both are refusals, not
     omissions: an enquiry that arrives already at "both trials done" is a record that
     lies about what happened, and an anchor is a two-way relationship that a form
     submission is not (SITE-RULES 2). The desk can route it to somebody, and routing
     is all that is. */
  async createLead(draft: NewLeadDraft): Promise<Lead> {
    const today = this.clock.today();
    const name = `${draft.firstName} ${draft.lastName}`.trim();
    const owner = draft.ownerId
      ? this.db.staff.find((x) => x.id === draft.ownerId)?.name ?? null
      : null;
    const lead: Lead = {
      id: uuidFrom('lead:added:' + this.db.leads.length),
      gymId: this.db.gym.id,
      memberId: null,
      firstName: draft.firstName, lastName: draft.lastName, name,
      email: draft.email, phone: draft.phone,
      photoUrl: null,
      stage: 'new',
      stageHistory: [{ stage: 'new', at: today }],
      owner, ownerId: draft.ownerId,
      interest: draft.interest,
      /* A seat that is not shown the value does not get to set one. The form does
         not ask; this is why asking anyway would not work. */
      valueCents: this.showsMoney ? draft.valueCents : null,
      source: draft.source, sourceDetail: draft.sourceDetail,
      heard: draft.heard,
      why: null, goal: null,
      createdAt: today,
      lastActivity: today,
      nextAction: 'First response within the hour, from a named person',
      lostReason: null, lostReasonDetail: null,
      anchor: null,
      notes: [],
    };
    this.db.leads.unshift(lead);
    this.append({
      memberId: null, leadId: lead.id,
      at: today, kind: 'lead_created', title: 'Enquiry',
      detail: [draft.source, draft.sourceDetail].filter(Boolean).join(' · ') || 'Added by hand',
      who: this.session?.user.name ?? null, source: 'Staff', tone: 'info', icon: 'star',
      payload: {},
    });
    return lead;
  }

  async getLead(id: UUID): Promise<Lead | null> {
    const l = this.db.leads.find((x) => x.id === id);
    return l ? this.seenLead(l) : null;
  }

  async updateLead(id: UUID, patch: Partial<Lead>): Promise<Lead> {
    const l = this.db.leads.find((x) => x.id === id);
    if (!l) throw new Error('No such lead: ' + id);
    /* A stage change is a DATED TRANSITION, or the board's trend is decoration. */
    if (patch.stage && patch.stage !== l.stage) {
      l.stageHistory = [...l.stageHistory, { stage: patch.stage, at: this.clock.today() }];
    }
    Object.assign(l, patch);
    l.lastActivity = this.clock.today();
    return this.seenLead(l);
  }

  /* A note on a lead is the SAME kind of thing as a note on a member: it goes on the
     one event stream, and it is also what the lead track reads as evidence that
     somebody actually spoke to them. Written in both places from here so the two can
     never disagree - the shipped demo appended to `l.notes` and called recordEvent()
     from the screen, and a screen that forgets the second call is a conversation the
     cadence never hears about. */
  async addLeadNote(id: UUID, text: string): Promise<TimelineEvent> {
    const l = this.db.leads.find((x) => x.id === id);
    if (!l) throw new Error('No such lead: ' + id);
    const who = this.session?.user.name ?? l.owner ?? 'Staff';
    const at = this.clock.today();
    l.notes = [...l.notes, { at, who, text }];
    l.lastActivity = at;
    return this.append({
      memberId: null, leadId: l.id,
      at, kind: 'note', title: text, detail: '',
      who, source: 'Staff', tone: 'plain', icon: 'note',
      payload: { who },
    });
  }

  /* A LOST LEAD CARRIES A REASON, or it does not become lost.
   *
   * The migration found 57 leads closed with the bare word "Others" and 351 more
   * with the real reason typed into a column nobody reported on. A dropdown of the
   * reasons people actually give, plus their own words when none of them fit, is the
   * difference between a number somebody can act on and a field nobody can group. */
  async setLeadStage(id: UUID, stage: LeadStage, lost?: { reason: string; detail: string }): Promise<Lead> {
    const l = this.db.leads.find((x) => x.id === id);
    if (!l) throw new Error('No such lead: ' + id);
    if (stage === 'lost' && !lost?.reason.trim()) {
      throw new Error('Say why they did not join. A lead closed without a reason teaches nobody anything.');
    }
    const patch: Partial<Lead> = { stage };
    if (stage === 'lost') {
      patch.lostReason = lost!.reason;
      patch.lostReasonDetail = lost!.detail.trim() || null;
    }
    const before = l.stage;
    await this.updateLead(id, patch);
    this.append({
      memberId: null, leadId: l.id,
      at: this.clock.today(),
      kind: stage === 'won' ? 'converted' : stage === 'lost' ? 'cancelled' : 'contacted',
      title: `Stage: ${before} to ${stage}`,
      detail: stage === 'lost' ? [lost!.reason, lost!.detail].filter(Boolean).join(' · ') : '',
      who: this.session?.user.name ?? null, source: 'Staff',
      tone: stage === 'won' ? 'good' : stage === 'lost' ? 'risk' : 'info',
      icon: 'flag', payload: {},
    });
    return l;
  }

  async bookTrial(id: UUID, classId: UUID, date: ISODate): Promise<Lead> {
    const l = this.db.leads.find((x) => x.id === id);
    if (!l) throw new Error('No such lead: ' + id);
    const cls = this.db.classes.find((c) => c.id === classId);
    if (!cls) throw new Error('No such class');
    const who = this.session?.user.name ?? l.owner ?? 'Staff';
    const text = `Trial booked: ${cls.name}, ${cls.time} on ${date}`
      + (cls.instructor ? ` with ${cls.instructor}` : '');
    /* Through addLeadNote, so the booking lands on the record AND on the one stream
       and therefore counts as evidence against the lead track's day-2 checkpoint. */
    await this.addLeadNote(id, text);
    this.append({
      memberId: null, leadId: l.id,
      at: this.clock.today(), kind: 'trial_booked', title: 'Trial booked',
      detail: `${cls.name} · ${cls.time} · ${date}`,
      who, source: 'Staff', tone: 'good', icon: 'star',
      payload: { classId, date },
    });
    /* Booking does NOT move the stage. They have not attended yet, and a board that
       showed them at "trial done" the moment somebody wrote a date down would be a
       board reporting a class that has not happened. */
    return l;
  }

  /* ── Classes and the register ──────────────────────────────────────────── */

  async getClasses(_date: ISODate): Promise<GymClass[]> {
    // One timetable, every day. A session is class + coach + time.
    return this.db.classes;
  }

  /* THE DAY, in one call.
   *
   * The shipped demo built this in the screen: it walked the whole roster and all of
   * their visits for every session, twice, and at 3,000 members that was 59% of the
   * render. It is the source's job because the source is the only side that holds the
   * history, and because the API will have to answer the same question with SQL. */
  async getSchedule(date: ISODate): Promise<Schedule> {
    const writable = date === this.clock.today();
    const sessions: SessionRow[] = this.timetable().map((cls) => ({
      cls,
      attended: this.attendees(cls.id, date).length,
      newFaces: this.seenAll(this.newFaces(cls)),
    }));

    /* Everybody on the day's registers, once, with the sessions they are on. The bar
       reads the SAME rows the registers do, which is the whole point: a name in a
       count across the top is always a name you can find on a register below. */
    const seen = new Map<UUID, ContextPerson>();
    for (const { cls } of sessions) {
      for (const row of this.registerRows(cls, date, writable)) {
        const label = `${cls.name} · ${cls.time}`;
        const had = seen.get(row.member.id);
        if (had) { if (!had.sessions.includes(label)) had.sessions.push(label); continue; }
        seen.set(row.member.id, { member: row.member, sessions: [label], context: row.context });
      }
    }

    const context: ContextGroup[] = CONTEXT_GROUPS.map((g) => ({
      ...g,
      people: [...seen.values()]
        .filter((p) => groupsFor(p.context, !!p.member.suppression).has(g.key))
        .sort((a, b) => a.member.name.localeCompare(b.member.name)),
    }));

    return { date, writable, sessions, context };
  }

  async getRegister(classId: UUID, date: ISODate): Promise<Register> {
    const cls = this.db.classes.find((c) => c.id === classId);
    if (!cls) throw new Error('No such class: ' + classId);
    const writable = date === this.clock.today();
    const rows = this.registerRows(cls, date, writable);
    return { date, cls, writable, rows, inCount: rows.filter((r) => r.in).length };
  }

  /** Idempotent by construction: same person, same session, same day, ONCE. The unique
   *  index in MIGRATION.md §4 says the same thing in the schema. */
  async checkIn(memberId: UUID, classId: UUID, date: ISODate): Promise<void> {
    const m = this.mustMember(memberId);
    const cls = this.db.classes.find((c) => c.id === classId);
    if (!cls) throw new Error('No such class: ' + classId);
    const events = this.byMember.get(m.id) ?? [];
    if (events.some((e) => e.kind === 'attendance' && e.at === date && e.payload['classId'] === classId)) return;
    const sessionKey = String(this.db.classes.indexOf(cls));
    this.append({
      memberId: m.id, leadId: null,
      at: date, kind: 'attendance', title: cls.name,
      detail: cls.time + (cls.instructor ? ' · ' + cls.instructor : ' · Self-guided'),
      who: cls.instructor, source: 'Register', tone: 'good', icon: 'check',
      payload: { sessionKey, classId, className: cls.name, time: cls.time, coach: cls.instructor, status: 'SignedIn' },
    });
    this.recount(m);
  }

  async undoCheckIn(memberId: UUID, classId: UUID, date: ISODate): Promise<void> {
    const m = this.mustMember(memberId);
    const drop = (e: SeededEvent) => e.kind === 'attendance' && e.at === date && e.payload['classId'] === classId;
    this.db.events = this.db.events.filter((e) => !(e.memberId === m.id && drop(e)));
    this.byMember.set(m.id, (this.byMember.get(m.id) ?? []).filter((e) => !drop(e)));
    this.recount(m);
  }

  /* Context that nobody writes down is context that dies in the room. A note taken at
     the register goes on the ONE stream, so the member's timeline is the single record
     of what was said, wherever it was said. A follow-up is a chase, so it is never
     offered on a suppressed member and is refused here as well as hidden there. */
  async addRegisterNote(memberId: UUID, classId: UUID, text: string, kind: 'note' | 'followup'): Promise<void> {
    const m = this.mustMember(memberId);
    const cls = this.db.classes.find((c) => c.id === classId);
    if (kind === 'followup' && m.suppression) {
      throw new Error('A member on Care Track or Hold is never chased');
    }
    this.append({
      memberId: m.id, leadId: null,
      at: this.clock.today(),
      kind: kind === 'followup' ? 'followup' : 'note',
      title: text,
      detail: cls ? `${cls.name} · ${cls.time}` : '',
      who: this.session?.user.name ?? null, source: 'Register',
      tone: kind === 'followup' ? 'warn' : 'plain',
      icon: kind === 'followup' ? 'flag' : 'note',
      payload: { who: this.session?.user.name ?? 'Staff', ...(classId ? { classId } : {}) },
    });
  }

  /* ── How the register is worked out ────────────────────────────────────── */

  /** Sorted the way a timetable is read: by the clock, not by insertion order. */
  private timetable(): GymClass[] {
    return this.db.classes.slice().sort((a, b) => minsOf(a.time) - minsOf(b.time));
  }

  private attendees(classId: UUID, date: ISODate): Member[] {
    const out: Member[] = [];
    for (const m of this.db.members) {
      const events = this.byMember.get(m.id) ?? [];
      if (events.some((e) => e.kind === 'attendance' && e.at === date && e.payload['classId'] === classId)) out.push(m);
    }
    return out;
  }

  /** The session somebody most often attends: their home class. */
  private homeSession(m: Member): UUID | null {
    const n = new Map<UUID, number>();
    for (const e of this.byMember.get(m.id) ?? []) {
      if (e.kind !== 'attendance') continue;
      const id = e.payload['classId'] as UUID | undefined;
      if (id) n.set(id, (n.get(id) ?? 0) + 1);
    }
    let best: UUID | null = null;
    let top = 0;
    for (const [id, c] of n) if (c > top) { top = c; best = id; }
    return best;
  }

  /** First-90-day members whose home session this is, newest joiner first. */
  private newFaces(cls: GymClass): Member[] {
    const today = this.clock.today();
    return this.db.members
      .filter((m) => {
        if (m.status === 'cancelled') return false;
        const d = daysSince(m.joinedAt, today);
        return d != null && d <= ONBOARDING_DAYS && this.homeSession(m) === cls.id;
      })
      .sort((a, b) => (daysSince(a.joinedAt, today) ?? 0) - (daysSince(b.joinedAt, today) ?? 0));
  }

  /* WHO TO PUT ON THE REGISTER.
   *
   * There is no booking system to read, so the roster is the people who actually come
   * to this class: everyone with a visit to it in the last 60 days, most frequent
   * first. CAPPED, because a 20-seat class has ~118 distinct people through it in 60
   * days and 118 checkboxes is a scroll, which is the cost the checkbox was meant to
   * remove. Anybody else is found by name; rare things are allowed to cost keystrokes.
   *
   * A PAST DAY IS A CLOSED RECORD: it shows who was there, with no empty boxes to
   * tick. Today shows the roster, so the job is a column of ticks. */
  private registerRows(cls: GymClass, date: ISODate, writable: boolean): RegisterRow[] {
    const here = this.attendees(cls.id, date);
    const inRoom = new Set(here.map((m) => m.id));

    let people: Member[];
    if (!writable) {
      people = [...here, ...this.inducting(cls, date)];
    } else {
      /* The first-90-day members go on BEFORE the cap: they are the names the table's
         New column just showed, and a register that cuts them for a more frequent
         regular contradicts its own table. */
      const fresh = this.newFaces(cls);
      const room = Math.max(12, Math.round((cls.cap ?? 20) * 2));
      const taken = new Set(fresh.map((m) => m.id));
      const regulars = this.regulars(cls, date)
        .filter((m) => !taken.has(m.id))
        .slice(0, Math.max(0, room - fresh.length));
      people = [...this.inducting(cls, date), ...fresh, ...regulars, ...here];

      /* A self-guided session has no history at all and would open an empty register.
         Top a thin roster up with whoever has been in the building most recently,
         which for Open Gym is the right list anyway. */
      if (people.length < 8) {
        const on = new Set(people.map((m) => m.id));
        people = people.concat(
          this.db.members
            .filter((m) => !on.has(m.id) && m.lastVisit && m.status !== 'cancelled')
            .sort((a, b) => String(b.lastVisit).localeCompare(String(a.lastVisit)))
            .slice(0, 24 - people.length),
        );
      }
    }

    const seen = new Set<UUID>();
    /* ALPHABETICAL, and it does NOT reorder as you tick: a list that resorts under
       your thumb costs you your place. */
    return people
      .filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((m) => {
        const facts = this.factsFor(m, date, inRoom.has(m.id));
        return {
          member: this.seen(m),
          in: inRoom.has(m.id),
          facts,
          context: registerContext(m, facts, date, { model: this.db.meta.riskModel }),
        };
      });
  }

  private regulars(cls: GymClass, date: ISODate): Member[] {
    const cutoff = this.clock.daysAgo(60);
    const scored: { m: Member; n: number }[] = [];
    for (const m of this.db.members) {
      if (m.status === 'cancelled') continue;
      let n = 0;
      for (const e of this.byMember.get(m.id) ?? []) {
        if (e.kind === 'attendance' && e.payload['classId'] === cls.id && e.at >= cutoff && e.at <= date) n++;
      }
      if (n) scored.push({ m, n });
    }
    return scored.sort((a, b) => b.n - a.n).map((x) => x.m);
  }

  /** Somebody inducting today has no attendance yet, so the derived roster cannot know
   *  about them. Their first class is the one thing the gym DOES know in advance, and
   *  it is the row where context is worth the most. */
  private inducting(cls: GymClass, date: ISODate): Member[] {
    return this.db.members.filter((m) => {
      const fc = m.firstClass;
      return !!fc && fc.date === date && fc.className === cls.name && (!fc.time || fc.time === cls.time);
    });
  }

  private factsFor(m: Member, date: ISODate, inRoom: boolean): RegisterFacts {
    const events = this.byMember.get(m.id) ?? [];
    const visits = events.filter((e) => e.kind === 'attendance').map((e) => e.at.slice(0, 10));
    const prior = visits.filter((d) => d < date).sort().reverse();
    const week = visits.filter((d) => d <= date && (daysSince(d, date) ?? 99) <= 6).length;

    /* The last time a member of STAFF actually spoke to them. A member anchor is
       somebody else's friendship, not the gym's contact, so it does not count. */
    const touches: string[] = [];
    for (const e of events) {
      if (e.kind === 'note' || e.kind === 'call' || e.kind === 'sms' || e.kind === 'email'
        || e.kind === 'outcome' || e.kind === 'followup') touches.push(e.at.slice(0, 10));
    }
    for (const a of m.anchors) if (a.kind === 'staff') touches.push(a.lastTwoWay);
    const lastTouch = touches.filter((d) => d <= date).sort().reverse()[0] ?? null;

    return {
      nth: prior.length + 1,
      gap: prior.length ? daysSince(prior[0]!, date) : null,
      week,
      lastTouch,
      inRoom,
    };
  }

  /* ── Follow-ups (SITE-RULES 11d) ───────────────────────────────────────── */

  async getFollowUps(q: { ownerId?: UUID; status?: FollowUpStatus } = {}): Promise<FollowUp[]> {
    let rows = this.db.followUps.slice();
    if (q.ownerId) rows = rows.filter((f) => f.ownerId === q.ownerId);
    if (q.status) rows = rows.filter((f) => f.status === q.status);
    return rows.sort((a, b) => String(a.due).localeCompare(String(b.due)));
  }

  /** The loop is only closed by an outcome, and recording one writes to the timeline. */
  async recordOutcome(id: UUID, outcome: string, detail: string): Promise<FollowUp> {
    const f = this.db.followUps.find((x) => x.id === id);
    if (!f) throw new Error('No such follow-up: ' + id);
    f.outcome = outcome;
    f.outcomeDetail = detail;
    f.outcomeAt = this.clock.today();
    f.status = 'completed';
    this.append({
      memberId: f.memberId ?? null, leadId: f.leadId ?? null,
      at: f.outcomeAt, kind: 'outcome', title: outcome, detail,
      who: f.owner, source: 'Follow-up', tone: 'good', icon: 'reply',
      payload: { followUpId: f.id },
    });
    return f;
  }

  /* ── The aggregates ────────────────────────────────────────────────────── */

  /** The one aggregate endpoint. It never ships the roster. */
  async getDashboard(seat: Role): Promise<DashboardPayload> {
    const me = this.session?.user ?? null;
    const today = this.clock.today();
    const { people: mine, scoped } = this.scopeFor(seat, me);

    const current = mine.filter((m) => m.status !== 'cancelled');

    /* CANCELLATION INITIATED COMES OFF THE TOP, before any other split (PRD 4).
       It has to be first for the same reason suppression is: everything downstream
       either scores people or chases them, and this population must be in neither.
       Unlike suppression it is not removed from view - it is promoted to its own
       panel above everything else, which is the opposite direction for one rule.

       It is a SCOPE, not a permission: a floor seat gets an empty list because the
       Owner speaks, not because a flag was withheld. The Manager sees it because
       PRD 7 puts it on the manager and owner views both. */
    const seesCancellations = seat === 'gym_admin' || seat === 'manager';
    const cancellations = seesCancellations ? openCancellations(current) : [];
    const cancellingIds = new Set(cancellations.map((m) => m.id));

    /* Care Track and Hold, minus anybody who has asked to cancel: they are shown
       once, at the top, rather than twice in two lists that would disagree about
       which fact about them matters most. */
    const suppressed = current.filter((m) => m.suppression && !cancellingIds.has(m.id));
    /* SPLIT THEM OUT FIRST, before anything downstream can pick them up. Somebody
       who has raised cancelling is off the model exactly as a suppressed member is.
       Their `risk` is already null, so leaving them in here would not have produced
       a score - it would only have let them count as Green in the tier mix. */
    const notSuppressed = current.filter((m) => !m.suppression && !cancellingIds.has(m.id));
    /* Two populations are off the risk model for the same reason: the model declines
       to score what it cannot yet see. Suppressed members are away, not gone;
       first-90-day members have no rhythm to drift from. The member record says
       exactly this, and a console that still ranked them would contradict it about
       the same person. */
    const onboarding = notSuppressed.filter((m) => notScoredBecause(m, today) != null);
    const onboardingIds = new Set(onboarding.map((m) => m.id));
    const scorable = notSuppressed.filter((m) => !onboardingIds.has(m.id));

    /* DORMANT IS NOT THIS WEEK'S CALL (Anuj, 2026-09-15: "flag it but kind of ignore
       it"). Somebody eleven months gone scores Red on absence alone and would sit at
       the top of a list meant for members who drifted last fortnight — burying the
       people a phone call still reaches under the people it will not. They are
       counted and named in their own segment instead. isDormant is the model's, so
       the console and the Segments screen cannot disagree about who is in it. */
    const dormant = scorable.filter((m) => isDormant(m, today));
    const dormantIds = new Set(dormant.map((m) => m.id));
    const chaseable = scorable.filter((m) => !dormantIds.has(m.id));

    const goingQuiet = chaseable
      .filter((m) => (m.risk?.score ?? 101) <= SCORE_RED)
      .sort((a, b) => (a.risk?.score ?? 101) - (b.risk?.score ?? 101))
      .slice(0, 10);

    const quietIds = new Set(goingQuiet.map((m) => m.id));
    const unanchored = notSuppressed
      .filter((m) => !m.anchors.length && !quietIds.has(m.id) && !dormantIds.has(m.id))
      .sort((a, b) => String(b.joinedAt ?? '').localeCompare(String(a.joinedAt ?? '')))
      .slice(0, 10);

    /* A null review date is the worst case, not a tidy absence: it is a member who
       can be forgotten. It leads the list rather than sorting to the end. */
    const careReviews = suppressed
      .map((m) => {
        const due = m.suppression?.reviewAt ?? null;
        return { member: m, due, overdueBy: due ? daysSince(due, today) : null };
      })
      .sort((a, b) => {
        if ((a.due == null) !== (b.due == null)) return a.due == null ? -1 : 1;
        return (b.overdueBy ?? 0) - (a.overdueBy ?? 0);
      });

    const tiers = { Red: 0, Amber: 0, Green: 0 };
    for (const m of scorable) tiers[m.risk?.tier ?? 'Green']++;

    /* A FLOOR SEAT SEES WHAT IS OWED TO THE PEOPLE IN ITS BOOK. A manager and an
       owner see the floor's whole queue.
 
       Scoped by OWNER it read "nothing owed" on two of the four consoles, for two
       different reasons: an owner does not personally own reach-outs, and neither
       does a coach - they are the support coach's to make. But a coach absolutely
       wants to know what is owed to the people who train with them, whoever owes
       it, because they are the one who will see that person on Thursday.
 
       So the scope is the BOOK, not the queue. For a support coach the two are the
       same set; for a coach they are not, and the book is the useful one. */
    const mineIds = new Set(mine.map((m) => m.id));
    const scopedQueue = seat === 'sales' || seat === 'coach';
    const followUps = (await this.getFollowUps())
      .filter((f) => f.status !== 'completed' && f.status !== 'dismissed')
      .filter((f) => !scopedQueue || !me
        || (f.memberId ? mineIds.has(f.memberId) : f.ownerId === me.id))
      .slice(0, 20);

    const openLeads = this.db.leads.filter((l) => l.stage !== 'won' && l.stage !== 'lost');
    const mrrCents = current
      .filter((m) => m.status === 'active')
      .reduce((a, m) => a + (m.priceCents ?? 0), 0);

    return {
      stats: {
        members: current.length,
        active: current.filter((m) => m.status === 'active').length,
        suspended: suppressed.length,
        goingQuiet: scorable.filter((m) => (m.risk?.score ?? 101) <= SCORE_RED).length,
        openLeads: openLeads.length,
        /* Not sent to a seat that may not see prices. It is not rendered there
           either, but a number in a payload is a number one refactor away from a
           screen. */
        ...(this.showsMoney ? { mrrCents } : {}),
        visits30d: scorable.reduce((a, m) => a + m.visits30d, 0),
      },
      cancellations: this.seenAll(cancellations),
      goingQuiet: this.seenAll(goingQuiet),
      unanchored: this.seenAll(unanchored),
      followUps,
      journey: await this.getJourney(),
      careReviews: careReviews.map((r) => ({ ...r, member: this.seen(r.member) })),
      coverage: {
        covered: scorable.filter((m) => m.anchors.length > 0).length,
        of: scorable.length,
      },
      tiers,
      onboardingCount: onboarding.length,
      scoped,
      bookSize: current.length,
      riskModel: this.db.meta.riskModel,
      suppressedCount: suppressed.length,
    };
  }

  /** Lifecycle cells, counts AND the people behind them — computed by the ONE model in
   *  shared, off a projection of the event stream. */
  /* SITE-RULES 11e: a count must open its names. The strip carries only numbers on
     purpose, so the people come from the ONE set of predicates that produced the count
     (lifecycleCellPeople), never a second filter written to match. */
  async getJourneyCellPeople(
    key: string, q: { page?: number; size?: number } = {},
  ): Promise<CellPeople> {
    /* The key arrives as a string because it crosses HTTP in ApiSource's case. Validate
       at the boundary rather than widening the model's union. */
    if (!LIFECYCLE_CELLS.some((c) => c.key === key)) {
      return { members: emptyPage(q), leads: emptyPage(q) };
    }
    const people = lifecycleCellPeople(
      { members: this.memberRecords(), leads: this.db.leads as LeadRecord[], users: this.db.users },
      key as LifecycleCellKey,
      this.clock.today(),
      { model: this.db.meta.riskModel },
    );
    const members: Member[] = [];
    const leads: Lead[] = [];
    for (const p of people as Array<Member | Lead>) {
      if ('stage' in p) leads.push(p);
      else members.push(p);
    }
    return {
      members: pageOf(this.seenAll(members), q),
      leads: pageOf(this.seenLeads(leads), q),
    };
  }

  async getJourney(): Promise<JourneyCell[]> {
    const cells = lifecycleCounts(
      { members: this.memberRecords(), leads: this.db.leads as LeadRecord[], users: this.db.users },
      undefined,
      { today: this.clock.today(), model: this.db.meta.riskModel },
    );
    return cells.map((c) => {
      const cell: JourneyCell = {
        key: c.key, label: c.label, n: c.n, was: c.was, diff: c.diff,
        delta: c.delta, unit: c.unit, windowDays: c.windowDays,
        /* All nine open now. getJourneyCellPeople answers with leads as well as
           members, so the two lead cells and the mixed one no longer show a number
           and refuse the click. */
        opens: true,
      };
      if (c.tone) cell.tone = c.tone;
      if (c.good) cell.good = c.good;
      if (c.quiet) cell.quiet = c.quiet;
      return cell;
    });
  }

  /* ── The roster and the logins ─────────────────────────────────────────── */

  /* WHAT A MEMBER IS WORTH, AND WHAT THEY ARE WORTH TO THE ROOM.
   *
   * Two columns, never one score. The whole reason an owner asks this is to find
   * the members where the two disagree: the one on the cheapest plan who brought
   * three friends and is named by five people is not a cheap member, and the one on
   * the most expensive plan whom nobody names is not a safe one.
   *
   * Every figure traces to named people. `brought` is a referral on record; the
   * money beside it is what those people actually pay. `anchorFor` is the members
   * who recorded THEM as the person they know here, which is a two-way relationship
   * with a written basis, not a count of friendships (SITE-RULES 2). */
  async getWorth(q: { page?: number; size?: number; sort?: WorthSort } = {}): Promise<Page<Worth>> {
    const today = this.clock.today();
    const byId = new Map(this.db.members.map((m) => [m.id, m]));

    /* Who each member brought, built in ONE pass. Per-member it is O(n) against the
       roster and the owner's list is the whole roster. */
    const brought = new Map<UUID, Member[]>();
    const anchorFor = new Map<UUID, Member[]>();
    for (const m of this.db.members) {
      const rows = ((m.sf?.['referrals'] ?? []) as { direction?: string; otherMemberId?: UUID }[]);
      for (const r of rows) {
        if (!r.otherMemberId) continue;
        /* A row reads from the perspective of the member holding it. "made" means
           this member brought the other one; "received" means the other brought
           them. Both are the same edge, recorded once, and both have to be read or
           half the referrals in the gym are invisible. */
        const bringer = r.direction === 'made' ? m.id : r.otherMemberId;
        const brought_ = r.direction === 'made' ? r.otherMemberId : m.id;
        const who = byId.get(brought_);
        if (!who || bringer === brought_) continue;
        const list = brought.get(bringer) ?? [];
        if (!list.some((x) => x.id === who.id)) list.push(who);
        brought.set(bringer, list);
      }
      for (const a of m.anchors) {
        if (a.kind !== 'member' || !a.otherMemberId) continue;
        const list = anchorFor.get(a.otherMemberId) ?? [];
        if (!list.some((x) => x.id === m.id)) list.push(m);
        anchorFor.set(a.otherMemberId, list);
      }
    }

    const rows: Worth[] = this.db.members
      .filter((m) => m.status !== 'cancelled')
      .map((m) => {
        const b = brought.get(m.id) ?? [];
        return {
          member: m,
          paysCents: m.priceCents,
          months: Math.max(0, Math.floor((daysSince(m.joinedAt, today) ?? 0) / 30)),
          brought: b,
          broughtPaysCents: b.reduce((a, x) => a + (x.priceCents ?? 0), 0),
          anchorFor: anchorFor.get(m.id) ?? [],
        };
      });

    /* A MEMBER WHOSE PRICE NOBODY RECORDED CANNOT BE RANKED AGAINST WHAT THEY PAY.
       `?? 0` read "not recorded" as "pays nothing", which gave them the largest gap in
       the gym and put them at the top of the screen. Unknown is not free
       (SITE-RULES 3). They sort last: still there, still named, just not ranked. */
    const unpriced = (w: Worth): number => Number(w.paysCents == null);
    const gap = (w: Worth): number => w.broughtPaysCents - (w.paysCents ?? 0);
    const sort = q.sort ?? 'gap';
    rows.sort((a, b) => {
      const rankable = unpriced(a) - unpriced(b);
      switch (sort) {
        case 'pays': return rankable || (b.paysCents ?? 0) - (a.paysCents ?? 0);
        case 'brings': return b.broughtPaysCents - a.broughtPaysCents
          || b.brought.length - a.brought.length;
        case 'known': return b.anchorFor.length - a.anchorFor.length;
        /* THE DEFAULT IS THE DISAGREEMENT, because that is the question. The people
           who bring the most relative to what they pay come first; it is an ordering
           of two real quantities, not a score either of them is given.
           It used to add `anchorFor.length` into that subtraction - a count of people
           summed with an amount of money - which is exactly the blend the API's own
           header forbids, and it ordered this screen differently from the server's.
           Two recorded amounts, subtracted; the anchor count breaks ties only. */
        default: return rankable || gap(b) - gap(a) || b.anchorFor.length - a.anchorFor.length;
      }
    });

    const size = Math.min(Math.max(1, q.size ?? DEFAULT_PAGE_SIZE), MAX_PAGE);
    const page = Math.max(1, q.page ?? 1);
    const start = (page - 1) * size;
    return { items: rows.slice(start, start + size), total: rows.length, page, size };
  }

  async getStaff(): Promise<StaffView[]> {
    /* Counted once over the stream, not once per trainer: the shipped screen walked
       the whole roster per row and it was the slowest table in the app. */
    const visits = new Map<string, number>();
    const last = new Map<string, string>();
    for (const e of this.db.events) {
      if (e.kind !== 'attendance') continue;
      const coach = e.payload['coach'];
      if (typeof coach !== 'string') continue;
      visits.set(coach, (visits.get(coach) ?? 0) + 1);
      const had = last.get(coach);
      if (!had || e.at > had) last.set(coach, e.at.slice(0, 10));
    }
    const owns = new Map<string, number>();
    for (const m of this.db.members) {
      if (m.coach) owns.set(m.coach, (owns.get(m.coach) ?? 0) + 1);
    }
    return this.db.staff.map((x) => {
      const u = this.db.users.find((y) => y.id === x.id);
      return {
        ...x,
        visits: visits.get(x.name) ?? 0,
        lastTaught: last.get(x.name) ?? null,
        members: owns.get(x.name) ?? 0,
        linked: u ? { id: u.id, email: u.email } : null,
      };
    });
  }

  /* WHO MAY GRANT WHAT IS DECIDED HERE. An Owner may hand out every gym role; a
     Manager lives inside {Support Coach, Coach} and cannot see past it; everybody else
     grants nothing. `super_admin` is deliberately absent from both: it is the Acrostic
     HQ seat, it is not a gym role, and no screen may ever grant it.

     NOBODY CAN LOCK THEMSELVES OUT, and the gym's LAST OWNER cannot be removed or
     demoted. Both are refusals here rather than only hidden buttons: the buttons are a
     courtesy, these are the rule. */

  async getAccounts(): Promise<Account[]> {
    const me = this.session?.user ?? null;
    const grantable = grantableBy(me);
    const owners = this.db.users.filter((u) => u.roles.includes('gym_admin')).length;
    return this.db.users.map((u) => ({
      id: u.id, name: u.name, email: u.email, roles: u.roles,
      isSelf: !!me && u.id === me.id,
      lastOwner: u.roles.includes('gym_admin') && owners <= 1,
      /* Manageable means every role they hold is one this seat may grant. Otherwise a
         Manager could edit an Owner by removing the role they cannot see. */
      manageable: u.roles.length > 0 && u.roles.every((r) => grantable.includes(r)),
      /* Resolved, so the screen never has to know that "no set" means "ask the seat". */
      permissions: permissionsOf(u.roles, u.permissions ?? null),
      custom: !isDefaultFor(u.roles, u.permissions ?? null),
      photoUrl: u.photoUrl ?? null,
    }));
  }

  async createAccount(draft: { name: string; email: string; roles: Role[] }): Promise<{ account: Account; password: string }> {
    const grantable = grantableBy(this.session?.user ?? null);
    const email = draft.email.trim().toLowerCase();
    if (!draft.name.trim()) throw new Error('A name is required');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error('That is not an email address');
    if (this.db.users.some((u) => u.email.toLowerCase() === email)) throw new Error('That email already has an account');
    if (!draft.roles.length) throw new Error('An account with no seat cannot sign in to anything');
    for (const r of draft.roles) {
      if (!grantable.includes(r)) throw new Error(`You cannot hand out the ${r} seat`);
    }
    const u: User = {
      id: uuidFrom('user:added:' + this.db.users.length),
      gymId: this.db.gym.id, name: draft.name.trim(), email,
      roles: draft.roles, photoUrl: null,
    };
    this.db.users.push(u);
    const accounts = await this.getAccounts();
    return { account: accounts.find((a) => a.id === u.id)!, password: DEMO_PASSWORD };
  }

  /* The demo half of per-person permissions. The refusals that matter are the
     server's - this keeps the seeded source behaving like it so a screen cannot be
     written against the demo and be wrong in production. */
  async setAccountPermissions(id: UUID, permissions: Permission[] | null): Promise<Account> {
    const me = this.session?.user ?? null;
    const u = this.db.users.find((x) => x.id === id);
    if (!u) throw new Error('No such account');
    const mine = permissionsOf(me?.roles, me?.permissions ?? null);
    if (permissions) {
      for (const p of permissions) {
        if (!mine.includes(p)) throw new Error(`You cannot hand out "${p}"`);
      }
      for (const p of permissionsOf(u.roles, u.permissions ?? null)) {
        if (!permissions.includes(p) && !mine.includes(p)) {
          throw new Error(`You cannot take away "${p}"`);
        }
      }
    }
    const after = permissionsOf(u.roles, permissions ?? null);
    if (me && u.id === me.id && !after.includes('accounts')) {
      throw new Error('You cannot take away your own ability to manage staff');
    }
    u.permissions = permissions ?? null;
    return (await this.getAccounts()).find((a) => a.id === id)!;
  }

  async setAccountRoles(id: UUID, roles: Role[]): Promise<Account> {
    const me = this.session?.user ?? null;
    const u = this.db.users.find((x) => x.id === id);
    if (!u) throw new Error('No such account');
    if (!roles.length) throw new Error('An account with no seat cannot sign in to anything');
    const grantable = grantableBy(me);
    if (me && u.id === me.id && !u.roles.every((r) => roles.includes(r))) {
      throw new Error('You cannot take your own seat away');
    }
    const owners = this.db.users.filter((x) => x.roles.includes('gym_admin')).length;
    if (u.roles.includes('gym_admin') && owners <= 1 && !roles.includes('gym_admin')) {
      throw new Error('A gym has to keep one Owner');
    }
    for (const r of [...roles, ...u.roles]) {
      if (!grantable.includes(r)) throw new Error(`You cannot change the ${r} seat`);
    }
    u.roles = roles;
    return (await this.getAccounts()).find((a) => a.id === id)!;
  }

  /** In the demo every account signs in with the same word, and the dialog says so
   *  rather than printing a generated string the demo cannot actually enforce. */
  async resetAccountPassword(id: UUID): Promise<string> {
    if (!this.db.users.some((u) => u.id === id)) throw new Error('No such account');
    return DEMO_PASSWORD;
  }

  async removeAccount(id: UUID): Promise<void> {
    const me = this.session?.user ?? null;
    const u = this.db.users.find((x) => x.id === id);
    if (!u) throw new Error('No such account');
    if (me && u.id === me.id) throw new Error('You cannot remove your own account');
    const owners = this.db.users.filter((x) => x.roles.includes('gym_admin')).length;
    if (u.roles.includes('gym_admin') && owners <= 1) throw new Error('A gym has to keep one Owner');
    const grantable = grantableBy(me);
    for (const r of u.roles) {
      if (!grantable.includes(r)) throw new Error(`You cannot remove somebody holding the ${r} seat`);
    }
    this.db.users = this.db.users.filter((x) => x.id !== id);
  }

  /* ── Segments and campaigns ────────────────────────────────────────────── */

  async getSegments(): Promise<SegmentView[]> {
    return SEGMENTS.map((seg) => ({ ...seg, n: this.inSegment(seg.rule).length }));
  }

  async getSegmentMembers(
    id: string, q: { page?: number; size?: number } = {},
  ): Promise<Page<Member>> {
    const seg = SEGMENTS.find((x) => x.id === id);
    if (!seg) return emptyPage(q);
    /* The order is the MODEL's - risk-ranked segments come back worst first, because
       the score counts down from 100 - so the page must not re-sort it. */
    return pageOf(this.seenAll(this.inSegment(seg.rule)), q);
  }

  async getCampaigns(): Promise<CampaignView[]> {
    return this.db.campaigns.map((c) => {
      const audience = this.audienceOf(c.segmentId);
      return {
        ...c,
        audience: audience.length,
        segment: c.segmentId
          ? SEGMENTS.find((s) => s.id === c.segmentId)?.name ?? 'A segment that no longer exists'
          : EVERYONE,
        stats: clampStats(c, audience.length),
      };
    });
  }

  async saveCampaign(draft: CampaignDraft): Promise<Campaign> {
    /* A campaign may never be pointed at a list that is not an audience. Refused here
       rather than only hidden in the picker: the picker is a courtesy, this is the
       rule (SITE-RULES 1). */
    const seg = draft.segmentId ? SEGMENTS.find((x) => x.id === draft.segmentId) : null;
    if (draft.segmentId && !seg) throw new Error('No such segment');
    if (seg && !seg.sendable) throw new Error(`${seg.name} is a review list, never an audience`);

    const existing = draft.id ? this.db.campaigns.find((c) => c.id === draft.id) : undefined;
    if (existing) {
      Object.assign(existing, {
        name: draft.name, type: draft.type, channel: draft.channel,
        segmentId: draft.segmentId, subject: draft.subject, body: draft.body,
      });
      return existing;
    }
    const made: Campaign = {
      id: uuidFrom('campaign:added:' + this.db.campaigns.length),
      gymId: this.db.gym.id,
      name: draft.name, type: draft.type, channel: draft.channel,
      status: 'draft', segmentId: draft.segmentId,
      subject: draft.subject, body: draft.body,
      createdAt: this.clock.today(),
      /* A new campaign has sent nothing, and sending is off, so it never will until
         somebody switches it on. Zeros are the truth here, not a placeholder. */
      stats: { sent: 0, opened: 0, clicked: 0, replied: 0, converted: 0 },
    };
    this.db.campaigns.unshift(made);
    return made;
  }

  async deleteCampaign(id: UUID): Promise<void> {
    this.db.campaigns = this.db.campaigns.filter((c) => c.id !== id);
  }

  async dryRunCampaign(id: UUID): Promise<DryRun> {
    const c = this.db.campaigns.find((x) => x.id === id);
    if (!c) throw new Error('No such campaign: ' + id);
    const people = this.audienceOf(c.segmentId);
    /* Somewhere to send it, on the channel this campaign uses. Counted over the whole
       audience rather than the preview: the headline is about all of them. */
    const reachable = (m: Member): boolean =>
      c.channel === 'sms' ? !!m.phone
      : c.channel === 'email' ? !!m.email
      : !!m.email || !!m.phone;
    return {
      total: people.length,
      unreachable: people.filter((m) => !reachable(m)).length,
      preview: people.slice(0, 25).map((m) => ({
        name: m.name, email: m.email, phone: m.phone, message: this.render(c.body, m),
      })),
    };
  }

  async updateSettings(patch: Record<string, unknown>): Promise<Gym> {
    /* SENDING IS NOT A TOGGLE THE GYM OWNS. It is switched on by Acrostic once the
       domain is verified and a number is provisioned, and a demo that let somebody
       flick it would be promising a delivery it cannot make. */
    if ('emailSending' in patch || 'smsSending' in patch) {
      throw new Error('Acrostic switches sending on once the channel is set up');
    }
    /* WHO SEES WHAT A MEMBER PAYS IS THE OWNER'S CALL, and refused here rather than
       only disabled in the form. A manager who cannot grant themselves anything new
       could still have granted it to the floor. */
    if ('canSeeCommercials' in patch) {
      if (!canSetVisibility(this.session?.user.roles)) {
        throw new Error('Only the owner changes who sees what a member pays');
      }
      const next = patch['canSeeCommercials'];
      if (!Array.isArray(next) || !next.includes('gym_admin')) {
        throw new Error('The owner always sees them. Somebody has to.');
      }
    }
    this.db.gym.settings = { ...this.db.gym.settings, ...patch };
    return this.db.gym;
  }

  private inSegment(rule: SegmentRule): Member[] {
    return segmentMembers(this.db.members, rule, {
      today: this.clock.today(), model: this.db.meta.riskModel,
    });
  }

  /** Who a campaign would actually reach. No segment means active members who are NOT
   *  on Care Track or Hold: there is no "everyone", because an everyone audience is
   *  the easiest way to breach rule 1 by accident. */
  private audienceOf(segmentId: string | null): Member[] {
    if (!segmentId) return this.db.members.filter((m) => m.status === 'active' && !m.suppression);
    const seg = SEGMENTS.find((x) => x.id === segmentId);
    return seg ? this.inSegment(seg.rule) : [];
  }

  /** Merge fields, resolved off the record. `{{coach}}` is the person who actually
   *  teaches them, not a placeholder the gym has to remember to fill in. */
  private render(body: string, m: Member): string {
    return body
      .replace(/\{\{first_name\}\}/g, m.firstName)
      .replace(/\{\{gym_name\}\}/g, this.db.gym.name)
      .replace(/\{\{goal\}\}/g, (m.goal ?? 'your goals').toLowerCase())
      .replace(/\{\{coach\}\}/g, m.coach ?? m.supportCoach ?? 'your coach')
      .replace(/\{\{class\}\}/g, this.homeClassName(m) ?? 'class')
      .replace(/\{\{visits_30d\}\}/g, String(m.visits30d));
  }

  private homeClassName(m: Member): string | null {
    const id = this.homeSession(m);
    return id ? this.db.classes.find((c) => c.id === id)?.name ?? null : null;
  }

  async getApprovals(): Promise<ApprovalQueue> {
    /* The seeded queue is short enough to be its own total, and saying so in the
       shape rather than by returning a bare array is what keeps the screen from
       having to know which source it is talking to. */
    const items = this.db.approvals.filter((a) => !a.decision);
    return { items, total: items.length };
  }

  async decideApproval(id: UUID, decision: 'approved' | 'rejected'): Promise<void> {
    const a = this.db.approvals.find((x) => x.id === id);
    if (!a) throw new Error('No such approval: ' + id);
    a.decision = decision;
    if (decision !== 'approved') return;
    /* Approving APPLIES the change. Until 2026-09-04 resolving an approval only dropped
       the row, so "Approve" changed nothing on the record it named. */
    const patch = (a.payload?.['patch'] ?? null) as Record<string, unknown> | null;
    if (!patch) return;
    if (a.memberId) {
      const m = await this.getMember(a.memberId);
      if (m) {
        if (patch['status'] === 'cancelled' && !m.cancelledAt) m.cancelledAt = this.clock.today();
        await this.updateMember(a.memberId, patch as Partial<Member>);
      }
    } else if (a.leadId) {
      await this.updateLead(a.leadId, patch as Partial<Lead>);
    }
  }

  /* ── Internals ─────────────────────────────────────────────────────────── */

  private mustMember(id: UUID): Member {
    const m = this.db.members.find((x) => x.id === id);
    if (!m) throw new Error('No such member: ' + id);
    return m;
  }

  private append(e: Omit<SeededEvent, 'id'>): SeededEvent {
    const owner = e.memberId ?? e.leadId ?? 'x';
    const map = e.memberId ? this.byMember : this.byLead;
    const list = map.get(owner) ?? [];
    const ev: SeededEvent = { ...e, id: `${owner}:w${list.length}` };
    /* Ascending, like the stored stream: the record reverses it to render. */
    let i = list.length;
    while (i > 0 && (list[i - 1] as SeededEvent).at > ev.at) i--;
    list.splice(i, 0, ev);
    map.set(owner, list);
    this.db.events.push(ev);
    return ev;
  }

  /** Recount the 30-day window off the events, then recompute the cached score. */
  private recount(m: Member): void {
    const cutoff = this.clock.daysAgo(30);
    const events = this.byMember.get(m.id) ?? [];
    const visits = events.filter((e) => e.kind === 'attendance');
    m.visits30d = visits.filter((e) => e.at >= cutoff).length;
    m.lastVisit = visits.length ? (visits[visits.length - 1] as SeededEvent).at : null;
    this.rescore(m);
  }

  /** A CACHE of shared/model/risk.ts. Null for anybody `isScored` says holds none.
   *
   *  IT ASKS THE MODEL RATHER THAN LISTING THE CASES. This used to check suppression
   *  alone, so a rescore after any write - recording an anchor, editing a member -
   *  would put a number back on somebody who has asked to cancel, undoing by hand
   *  what recordCancellation() nulls by hand. Frozen and first-90-days members never
   *  had it taken off in the first place. */
  private rescore(m: Member): void {
    m.risk = !isScored(m, this.clock.today())
      ? null
      : (riskModel(m, {
        today: this.clock.today(),
        model: this.db.meta.riskModel,
        /* The gym's answer, asked once at build and carried here, so a rescore
           after a write scores the member the same way the build did. Without it
           recording an anchor would silently re-source an input for one member. */
        unsourced: this.db.meta.unsourced,
      }) as RiskResult);
  }

  /** The floor seats open on their OWN BOOK: the people who are theirs to know rather
   *  than the whole gym. A login that matches nobody - a live account before the
   *  roster is set up - falls back to the whole gym rather than an empty console, and
   *  says which it is doing, because a console that has quietly widened its scope is
   *  a console reporting somebody else's numbers as yours. */
  private scopeFor(seat: Role, me: User | null): { people: Member[]; scoped: boolean } {
    if (!me) return { people: this.db.members, scoped: false };
    const key = seat === 'sales' ? 'supportCoachId' : seat === 'coach' ? 'coachId' : null;
    if (!key) return { people: this.db.members, scoped: false };
    const mine = this.db.members.filter((m) => m[key] === me.id);
    return mine.length ? { people: mine, scoped: true } : { people: this.db.members, scoped: false };
  }

  /** Members with their history hung back on, for the model functions that rewind a
   *  board to what it was. A PROJECTION of the event stream, built on demand and thrown
   *  away — it is not how a member is stored. */
  private memberRecords(): MemberRecord[] {
    return this.db.members.map((m) => this.recordFor(m));
  }

  /* One member with their history hung off them, derived from the event stream.
   *
   * The LIST returns light members and the RECORD returns this: the same split the
   * PHP API makes with light_member(), and for the same reason. Shipping every
   * member's visits to draw a table of twenty-five names is how a roster gets slow,
   * and a record page that has to make four more round trips to answer "what has
   * happened with this person" is how a record page gets slow. */
  private recordFor(m: Member): MemberRecord {
    const events = this.byMember.get(m.id) ?? [];
    const visits: Visit[] = [];
    const scans: Scan[] = [];
    const notes: { at: ISODate; text: string; who: string }[] = [];
    for (const e of events) {
      if (e.kind === 'attendance') {
        visits.push({
          date: e.at.slice(0, 10),
          className: String(e.payload['className'] ?? e.title),
          coach: (e.payload['coach'] as string | null) ?? null,
          status: String(e.payload['status'] ?? 'SignedIn'),
        });
      } else if (e.kind === 'inbody') {
        const p = e.payload;
        scans.push({
          date: e.at.slice(0, 10),
          ...(typeof p['weight'] === 'number' ? { weight: p['weight'] } : {}),
          ...(typeof p['pbf'] === 'number' ? { pbf: p['pbf'] } : {}),
          ...(typeof p['smm'] === 'number' ? { smm: p['smm'] } : {}),
          ...(typeof p['score'] === 'number' ? { score: p['score'] } : {}),
        });
      } else if (e.kind === 'note') {
        notes.push({
          at: e.at.slice(0, 10),
          text: String(e.title ?? e.detail ?? ''),
          // `who` on a timeline event is who HANDLED it; null means the system observed it.
          who: String(e.payload['who'] ?? e.who ?? 'Staff'),
        });
      }
    }
    /* Newest first on a record: the last thing that happened is the thing somebody
       is asking about. */
    const desc = <T extends { date?: string; at?: string }>(rows: T[]) =>
      rows.sort((a, b) => String(b.date ?? b.at ?? '').localeCompare(String(a.date ?? a.at ?? '')));
    return { ...m, visits: desc(visits), scans: desc(scans), notes: desc(notes) };
  }
}

/* WON AND LOST ARE NOT ON THE BOARD. A won lead is a member and is read on the member
   record; a lost one is on a win-back cadence. Both are reachable by searching, which
   is what asking for history looks like. */
function matchLeadScope(stage: LeadStage, want: NonNullable<LeadQuery['scope']>): boolean {
  const closed = stage === 'won' || stage === 'lost';
  return want === 'all' ? true : want === 'closed' ? closed : !closed;
}

function matchStatus(s: MemberStatus, want: NonNullable<MemberQuery['status']>): boolean {
  switch (want) {
    case 'all': return true;
    /* CURRENT means active AND suspended. A member on Care Track or Hold is still a
       member: away is not gone, and dropping them from the default list is exactly how
       a safety feature turns into a filter. */
    case 'current': return s !== 'cancelled';
    case 'inactive': return s === 'cancelled';
    default: return s === want;
  }
}

function sortMembers(rows: Member[], sort?: string): Member[] {
  const desc = sort?.startsWith('-') ?? false;
  const key = (sort ?? 'name').replace(/^-/, '');
  const dir = desc ? -1 : 1;
  const out = rows.slice();
  out.sort((a, b) => {
    switch (key) {
      case 'risk': {
        /* A suppressed member has no score and is NEVER ranked. They sort to the end of
           either direction rather than being given a number to be ranked by. */
        if (!!a.suppression !== !!b.suppression) return a.suppression ? 1 : -1;
        if (a.suppression) return a.name.localeCompare(b.name);
        return dir * ((a.risk?.score ?? 101) - (b.risk?.score ?? 101));
      }
      case 'health': return dir * (a.health - b.health);
      case 'visits30d': return dir * (a.visits30d - b.visits30d);
      case 'joinedAt': return dir * String(a.joinedAt ?? '').localeCompare(String(b.joinedAt ?? ''));
      case 'lastVisit': return dir * String(a.lastVisit ?? '').localeCompare(String(b.lastVisit ?? ''));
      default: return dir * a.name.localeCompare(b.name);
    }
  });
  return out;
}

/* '6:00 AM' as minutes past midnight. A timetable is read by the clock, and sorting
   those strings puts 10:00 AM before 6:00 AM. */
export function minsOf(t: string): number {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(String(t).trim());
  if (!m) return 0;
  let h = Number(m[1]);
  const mi = Number(m[2]);
  const ap = (m[3] ?? '').toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + mi;
}

/** What "no segment" means, said in full rather than called "everyone". */
export const EVERYONE = 'Active members (not on Care Track or Hold)';

/* A COMPLETED CAMPAIGN CANNOT HAVE REACHED MORE PEOPLE THAN ARE IN ITS AUDIENCE.
   The stored numbers were sized against the roster at the time; the audience is
   derived now and moves, so the clamp lives at the point of reading. Active and draft
   campaigns are untouched: sending is off, so their zeros are the truth. */
function clampStats(c: Campaign, audience: number): Campaign['stats'] {
  const st = c.stats;
  if (c.status !== 'completed' || st.sent <= audience) return st;
  const k = audience / st.sent;
  const scale = (n: number): number => Math.min(audience, Math.round(n * k));
  return {
    sent: audience,
    opened: scale(st.opened), clicked: scale(st.clicked),
    replied: scale(st.replied), converted: scale(st.converted),
  };
}

/* One page out of a list already in hand. The page ceiling is the contract's, so a
   caller cannot ask this for more than the API would give it. */
function pageOf<T>(items: T[], q: { page?: number; size?: number }): Page<T> {
  const size = Math.min(Math.max(1, q.size ?? DEFAULT_PAGE_SIZE), MAX_PAGE);
  const page = Math.max(1, q.page ?? 1);
  const start = (page - 1) * size;
  return { items: items.slice(start, start + size), total: items.length, page, size };
}

function emptyPage<T>(q: { page?: number; size?: number }): Page<T> {
  return pageOf<T>([], q);
}

/* THE GYM ROLES, in the order they are offered. `super_admin` is deliberately absent:
   it is the Acrostic HQ seat, not a gym role, and no screen may ever grant it. */
export const ACCOUNT_ROLES: { key: Role; label: string; desc: string }[] = [
  { key: 'gym_admin', label: 'Owner', desc: 'Everything, including staff accounts, member medical notes and settings.' },
  { key: 'manager', label: 'Manager', desc: 'Runs the floor: approvals, members, leads, and Support Coach and Coach accounts.' },
  { key: 'sales', label: 'Support Coach', desc: 'Converts leads and keeps members known. Leads, members, no revenue figures.' },
  { key: 'coach', label: 'Coach', desc: 'Their own sessions and PT clients. Cannot assign themselves clients.' },
];

/* The rule is grantableBy() in @acrostic/model, because the API refuses by the same
   one. This is the User-shaped door onto it. */
export function grantableBy(me: User | null): Role[] {
  return seatsGrantableBy(me?.roles);
}

/* AN APPROVAL ROW HAS TO BE DECIDABLE FROM THE ROW. A manager signing one off is
   being asked "is this right?", and "Paige Clark: status, priceCents" does not
   contain the answer. One sentence per field, in the same shape as the queue's
   seeded rows, and money always with both numbers because the direction is the
   whole question. */
function describeChange(m: Member, patch: Partial<Member>, gated: (keyof Member)[]): string {
  const money = (c: number | null | undefined): string =>
    c == null ? 'nothing recorded' : '$' + (c / 100).toFixed(c % 100 === 0 ? 0 : 2);
  const parts: string[] = [];
  for (const k of gated) {
    if (k === 'status') {
      const to = patch.status;
      parts.push(to === 'cancelled' ? 'Cancel membership'
        : to === 'suspended' ? 'Put on hold'
        : 'Make active again');
    } else if (k === 'priceCents') {
      parts.push(`Price ${money(m.priceCents)} → ${money(patch.priceCents)}/mo`);
    } else if (k === 'plan') {
      parts.push(`Plan ${m.plan ?? 'none'} → ${patch.plan ?? 'none'}`);
    } else if (k === 'membershipExpires') {
      parts.push(`Renewal → ${patch.membershipExpires ?? 'none'}`);
    } else {
      parts.push(String(k));
    }
  }
  return parts.join(' · ');
}
